import React, {
  useEffect, useMemo, useRef, useState
} from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { useClickOutside, AccessibilityText, useTrapFocus } from '@glu/utilities-react';
import {
  LinesNextIcon, XIcon, MinusIcon, PlusIcon
} from '@glu/icons-react';
import { useTheme } from '@glu/theming';
import { Drawer } from '@glu/drawer-react';
import locale from '@glu/locale';
import useBodyScrollLock from './useBodyScrollLock';
import NavLink from './NavLink';
import './themeDefaults';
import useStyles from './SideNav.styles';

const isFixedOpen = (stayOpenAtWidth) => stayOpenAtWidth && window.innerWidth >= stayOpenAtWidth;

const SideNav = (props) => {
  const {
    alwaysAllowClose,
    children,
    className,
    closedLogo,
    collapsedSize,
    CollapseLinkIcon,
    dataQa,
    defaultOpenNavItem,
    expandedSize,
    ExpandLinkIcon,
    headerSpacing,
    LinkComponent,
    navLinks,
    onClose,
    onOpen,
    open: openProp,
    openedLinkPaddingBottom,
    openLogo,
    stayOpenAtWidth,
    subLinksAlwaysOpen
  } = props;
  const { nav } = useTheme();
  const navRef = useRef();
  const isFirstRender = useRef(true);
  const dialogRef = useRef();
  const classes = useStyles(
    useMemo(() => ({ stayOpenAtWidth, headerSpacing }), [stayOpenAtWidth, headerSpacing])
  );

  const [openNavItem, setOpenNavItem] = useState(defaultOpenNavItem
    ? defaultOpenNavItem(navLinks)
    : null);
  const [open, setOpen] = useState(openProp ?? false);

  const handleKeyDown = useTrapFocus({ ref: dialogRef, active: open });
  const handleOpenClick = () => {
    if (openProp === undefined) setOpen(true);
    onOpen();
  };
  const handleCloseClick = () => {
    if (openProp === undefined) setOpen(false);
    onClose();
  };
  const handleNavLinkClick = index => () => setOpenNavItem(openNavItem === index ? null : index);

  useClickOutside(navRef, handleCloseClick);

  useBodyScrollLock(open && !isFixedOpen(stayOpenAtWidth));

  useEffect(() => {
    /**
     * Without this check the navbar renders twice on initial mount. We want the equivalent of
     * componentDidUpdate. Without this check, it's a combination of
     * componentDidUpdate + componentDidMount
     */
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    setOpen(openProp);
  }, [openProp]);

  return (
    <nav
      className={classNames(classes.root, className)}
      data-qa={dataQa}
      ref={navRef}
    >
      {closedLogo ? (
        <div className={classNames(classes.logo, classes.smallLogo)}>{closedLogo}</div>
      ) : null}

      <button
        type="button"
        className={classes.hamburgerButton}
        onClick={handleOpenClick}
        data-qa="open-nav"
        aria-expanded={open}
      >
        <LinesNextIcon
          width={24}
          height={24}
          title={locale.get('nav.openNavigation')}
        />
        <AccessibilityText>{locale.get('nav.openNavigation')}</AccessibilityText>
      </button>

      {/* eslint-disable-next-line jsx-a11y/control-has-associated-label */}
      <div
        aria-hidden
        className={classNames({ [classes.drawerBlackout]: open })}
        onClick={handleCloseClick}
        onKeyPress={handleCloseClick}
        title={locale.get('nav.backToPage')}
      />
      {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions */}
      <div
        ref={dialogRef}
        onKeyDown={handleKeyDown}
        role="dialog"
        aria-modal="true"
        aria-label={locale.get('nav.label')}
      >
        <Drawer
          open={open}
          className={classes.panel}
          expandDuration={nav.expandDuration}
          collapseDuration={nav.collapseDuration}
          transitionMode={nav.transitionMode}
          startingSize={collapsedSize}
          size={expandedSize}
        >
          {open
            ? (
              <div className={classes.container} data-qa={`${dataQa || 'sidenav'}-panel`}>
                <div className={classes.header}>
                  <div className={classes.openLogo}>
                    {openLogo}
                  </div>

                  {(alwaysAllowClose || (!alwaysAllowClose && !stayOpenAtWidth)) && (
                  <button
                    className={classes.closeButton}
                    data-qa="close-button"
                    type="button"
                    onClick={() => (alwaysAllowClose ? handleCloseClick() : null)}
                  >
                    <XIcon className={classes.closeIcon} title={locale.get('nav.closeNavigation')} />
                    <AccessibilityText>{locale.get('nav.closeNavigation')}</AccessibilityText>
                  </button>
                  )}
                </div>

                <div className={classes.content}>
                  {headerSpacing ? <div data-qa="header-spacer" className={classes.headerSpacer} /> : null}

                  {children}
                  <ul className={classes.navLinks}>
                    {navLinks.map(({ closeNavOnClick = true, ...navLink }, index) => (
                      <NavLink
                        key={`side-navigation-root-${index}`} // eslint-disable-line react/no-array-index-key
                        open={openNavItem === index}
                        toggleOpen={handleNavLinkClick(index)}
                        alwaysOpen={subLinksAlwaysOpen}
                        LinkComponent={LinkComponent}
                        dataQa={dataQa}
                        closeNav={handleCloseClick}
                        openedLinkPaddingBottom={openedLinkPaddingBottom}
                        ExpandIcon={ExpandLinkIcon}
                        CollapseIcon={CollapseLinkIcon}
                        {...navLink}
                        onClick={(...args) => {
                          if (closeNavOnClick) { handleCloseClick(); }
                          if (typeof navLink.onClick === 'function') {
                            navLink.onClick(...args);
                          }
                        }}
                      >
                        {navLink.children}
                      </NavLink>
                    ))}
                  </ul>
                </div>
              </div>
            )
            : (
              <>
                {/* empty span element as Drawer children prop is required */}
                <span />
              </>
            )}
        </Drawer>
      </div>
    </nav>
  );
};

SideNav.propTypes = {
  /** Toggle whether to permit the user to close the SideNav
   * even when the window width is wider than the stayOpenAtWidth value
   */
  alwaysAllowClose: PropTypes.bool,
  /** Children to show inside the open drawer */
  children: PropTypes.node,
  /** A classname to apply to the navigation */
  className: PropTypes.string,
  /** Logo to show in the navigation when it is closed */
  closedLogo: PropTypes.node,
  /** A data-qa tag to apply to the navigation and links */
  dataQa: PropTypes.string,
  /**
   * This is a function that will return the default nav item to be
   * preselected when the navigation is opened.
   */
  defaultOpenNavItem: PropTypes.func,
  /** A number of pixels of space to have below the navigation header */
  headerSpacing: PropTypes.number,
  /** The component to use when rendering links in the navigation */
  LinkComponent: PropTypes.func,
  /** The set of navigation links to show */
  navLinks: PropTypes.arrayOf(PropTypes.shape({
    /** If false, the nav will not close on click of link */
    closeNavOnClick: PropTypes.bool
  })),
  /** Callback to fire when the navigation is opened */
  onOpen: PropTypes.func,
  /** Callback to fire when the navigation is closed */
  onClose: PropTypes.func,
  /**
   * If a boolean is passed in, the navigation will hand-off control of
   * it's open state to the parent component. If it is undefined, the
   * navigation will manage it's own open state.
   */
  open: PropTypes.bool,
  /** Logo to show in the navigation when it is open */
  openLogo: PropTypes.node,
  /** If provided, the navigation will stay open at this width */
  stayOpenAtWidth: PropTypes.number,
  /** Set if sublinks should always be open and visible */
  subLinksAlwaysOpen: PropTypes.bool,
  /** Expanded sublinks padding bottom size */
  openedLinkPaddingBottom: PropTypes.number,
  /** Expanded navigation size */
  expandedSize: PropTypes.number,
  /** Collapsed navigation size */
  collapsedSize: PropTypes.number,
  /** Icon what will be shown on expanded link */
  CollapseLinkIcon: PropTypes.func,
  /** Icon what will be shown on collapsed link */
  ExpandLinkIcon: PropTypes.func
};

SideNav.defaultProps = {
  alwaysAllowClose: false,
  children: undefined,
  className: undefined,
  closedLogo: undefined,
  dataQa: undefined,
  defaultOpenNavItem: undefined,
  headerSpacing: 0,
  LinkComponent: undefined,
  navLinks: [],
  onOpen: () => {},
  onClose: () => {},
  open: undefined,
  openLogo: undefined,
  stayOpenAtWidth: undefined,
  subLinksAlwaysOpen: false,
  openedLinkPaddingBottom: 13,
  expandedSize: 330,
  collapsedSize: 68,
  CollapseLinkIcon: MinusIcon,
  ExpandLinkIcon: PlusIcon
};

SideNav.displayName = 'SideNav';
export default SideNav;
